import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Users, UserPlus, Shield, Phone } from "lucide-react";
import { toast } from "sonner";

const emptyInvite = { name: "", email: "", password: "", role: "member" };

export default function Team() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyInvite);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/team/members").then((r) => { setTeam(r.data.team); setMembers(r.data.members); });
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function invite() {
    if (!form.name || !form.email || !form.password) return toast.error("Preencha todos os campos.");
    setSaving(true);
    try {
      await api.post("/team/invite", form);
      toast.success("Comercial adicionado à equipa");
      setOpen(false); setForm(emptyInvite); await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao convidar");
    } finally { setSaving(false); }
  }

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-white/10 bg-[#111827] overflow-hidden">
        <div className="relative h-32">
          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')", backgroundSize: "cover", backgroundPosition: "center" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111827] to-transparent" />
        </div>
        <div className="p-6 -mt-10 relative">
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Users className="w-7 h-7 text-[#2563EB]" /> {team?.name || "Equipa"}
          </h1>
          <p className="text-[#94A3B8] text-sm mt-1">{members.length} comercial(is) nesta equipa.</p>
        </div>
      </div>

      <div className="flex justify-end">
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="team-invite-member-button" className="bg-[#2563EB] hover:bg-[#1D4ED8]">
                <UserPlus className="w-4 h-4 mr-1.5" /> Adicionar comercial
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#111827] border-white/10">
              <DialogHeader><DialogTitle className="font-display">Adicionar comercial</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-[#94A3B8]">Nome</Label><Input value={form.name} onChange={set("name")} data-testid="invite-name-input" className="mt-1 bg-[#0B0F17] border-white/10" /></div>
                <div><Label className="text-[#94A3B8]">Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="invite-email-input" className="mt-1 bg-[#0B0F17] border-white/10" /></div>
                <div><Label className="text-[#94A3B8]">Palavra-passe inicial</Label><Input value={form.password} onChange={set("password")} data-testid="invite-password-input" className="mt-1 bg-[#0B0F17] border-white/10" /></div>
                <div>
                  <Label className="text-[#94A3B8]">Função</Label>
                  <select value={form.role} onChange={set("role")} className="w-full mt-1 h-10 rounded-md bg-[#0B0F17] border border-white/10 px-3 text-sm">
                    <option value="member">Comercial</option>
                    <option value="admin">Líder comercial (admin)</option>
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={invite} disabled={saving} data-testid="confirm-invite-button" className="bg-[#10B981] hover:bg-[#059669]">
                  {saving ? "A adicionar…" : "Adicionar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {members.map((m) => (
          <div key={m.id} className="rounded-2xl border border-white/10 bg-[#111827] p-5 flex items-center gap-4" data-testid={`member-card-${m.id}`}>
            <div className="w-12 h-12 rounded-full bg-[#1A2234] border border-white/10 flex items-center justify-center text-lg font-bold text-[#2563EB]">
              {m.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white truncate">{m.name}</div>
              <div className="text-xs text-[#64748B] truncate">{m.email}</div>
              <div className="flex items-center gap-3 mt-1.5">
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded flex items-center gap-1 ${m.role === "admin" ? "bg-[#2563EB]/20 text-[#2563EB]" : "bg-white/5 text-[#94A3B8]"}`}>
                  <Shield className="w-3 h-3" /> {m.role === "admin" ? "Líder" : "Comercial"}
                </span>
                <span className="text-[10px] text-[#64748B] flex items-center gap-1"><Phone className="w-3 h-3" /> {m.calls_count} chamadas</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
